
import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';

interface SearchableSelectProps {
  options: { value: string; label: string }[];
  value: string | null;
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Selecione...", 
  className = "",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter((opt) => 
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedLabel = options.find((opt) => opt.value === value)?.label;

  return (
    <div className={`relative ${disabled ? 'opacity-50 pointer-events-none' : ''}`} ref={wrapperRef}>
      <div 
        onClick={() => { if(!disabled) { setIsOpen(!isOpen); if (!isOpen) setSearchTerm(''); } }}
        className={`cursor-pointer flex items-center justify-between ${className}`}
      >
        <span className={`truncate ${!selectedLabel ? 'text-slate-400' : 'text-navy-900 dark:text-white'}`}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown size={16} className="text-slate-400 shrink-0 ml-2" />
      </div>
      
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-navy-900 border border-neutral-100 dark:border-navy-700 rounded-2xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2">
          <div className="p-2 border-b border-neutral-100 dark:border-navy-800 sticky top-0 bg-white dark:bg-navy-900">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-navy-950 rounded-xl border border-transparent focus-within:border-parliament-600 transition-colors">
               <Search size={14} className="text-slate-400" />
               <input 
                 autoFocus
                 value={searchTerm}
                 onChange={e => setSearchTerm(e.target.value)}
                 className="bg-transparent outline-none text-xs font-bold w-full dark:text-white placeholder-slate-400"
                 placeholder="Buscar..."
               />
               {searchTerm && <button onClick={() => setSearchTerm('')}><X size={12} className="text-slate-400 hover:text-red-500"/></button>}
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto custom-scrollbar p-1">
            {filteredOptions.length > 0 ? filteredOptions.map((opt) => (
              <div 
                key={opt.value} 
                onClick={() => { onChange(opt.value); setIsOpen(false); }}
                className={`px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-navy-800 rounded-xl text-xs font-bold cursor-pointer transition-colors ${value === opt.value ? 'bg-slate-100 dark:bg-navy-800 text-parliament-600' : 'text-slate-600 dark:text-slate-300'}`}
              >
                {opt.label}
              </div>
            )) : (
              <div className="p-4 text-center text-[10px] font-bold text-slate-400 uppercase">Sem resultados</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
